// The Skills page's rules, pure (catalogModel.selftest.ts): names as words,
// grouping and search, who has a skill in words, and what a switch in the
// drawer's "Who has it" sends.
//
// How a skill reaches an agent (backend services/skills_catalog.py):
//   - "everyone": every team agent, and the subagents whose own skill list
//     names it ("listed");
//   - "chosen": the agents picked for it, team agents and subagents alike;
//   - "own": its author's private skill.
// A family agent never holds one.

import type { CatalogAgent, CatalogSkill } from "@/api/skills";
import { formatDate } from "@/lib/format";

import { skillName } from "../skillText";
import { CONNECTIONS_UNDERNEATH, PURPOSES, builtIn, plainLine, purposeOf, type Purpose } from "./builtIns";

export type GroupKey = "yours" | "platform" | "library";

type Named = Pick<CatalogSkill, "group" | "name" | "slug" | "description">;

/**
 * A skill's name in words. A built-in's is the plain one in builtIns.ts
 * ("Slide decks" for lattice-deck); an unknown built-in drops the platform
 * prefix ("Memory" for lattice-memory).
 */
export function displayName(skill: Named): string {
  if (skill.group === "platform") {
    const known = builtIn(skill.slug);
    if (known) return known.name;
    const bare = skill.slug.replace(/^lattice[-_]/, "");
    return skillName(bare || skill.slug, skill.description) || skill.slug;
  }
  return skillName(skill.name || skill.slug, skill.description) || skill.slug;
}

/** What it does, in one plain line (a built-in's own words, else the first sentence of its description). */
export function skillLine(skill: Pick<CatalogSkill, "group" | "slug" | "description">): string {
  if (skill.group === "platform") {
    const known = builtIn(skill.slug);
    if (known) return known.line;
  }
  return plainLine(skill.description);
}

/** Every word of the query appears in the name, the line, the description or the slug. */
export function matches(skill: Named, query: string): boolean {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const hay = `${displayName(skill)} ${skillLine(skill)} ${skill.name} ${skill.slug} ${skill.description}`.toLowerCase();
  return words.every((w) => hay.includes(w));
}

export interface PurposeGroup {
  purpose: Purpose;
  title: string;
  skills: CatalogSkill[];
}

/** Built-in skills by what they are for, in PURPOSES order, empty groups left out; each by name. */
export function byPurpose(skills: readonly CatalogSkill[]): PurposeGroup[] {
  const out = new Map<Purpose, CatalogSkill[]>();
  for (const s of skills) {
    const p = purposeOf(s.slug);
    out.set(p, [...(out.get(p) ?? []), s]);
  }
  return PURPOSES.filter((p) => out.has(p.key)).map((p) => ({
    purpose: p.key,
    title: p.title,
    skills: out.get(p.key)!.sort((a, b) => displayName(a).localeCompare(displayName(b))),
  }));
}

export interface Grouped {
  yours: CatalogSkill[];
  platform: CatalogSkill[];
  library: CatalogSkill[];
}

/** The skills the Skills screens list: where Connections is on, without the built-ins that are Connections underneath. */
export function listedSkills<S extends Pick<CatalogSkill, "group" | "slug">>(skills: readonly S[], connections: boolean): S[] {
  return connections ? skills.filter((s) => !(s.group === "platform" && CONNECTIONS_UNDERNEATH.includes(s.slug))) : [...skills];
}

/** The catalog in its three groups, each by name, filtered by the search. */
export function groupCatalog(skills: readonly CatalogSkill[], query: string): Grouped {
  const out: Grouped = { yours: [], platform: [], library: [] };
  for (const s of skills) if (matches(s, query)) out[s.group].push(s);
  const byName = (a: CatalogSkill, b: CatalogSkill) => displayName(a).localeCompare(displayName(b));
  out.yours.sort(byName);
  out.platform.sort(byName);
  out.library.sort(byName);
  return out;
}

// ─── who has it ────────────────────────────────────────────────────────

export interface Holding {
  /** Every team agent, by the skill's own reach (not a list). */
  everyone: boolean;
  team: CatalogAgent[];
  subagents: CatalogAgent[];
}

export function holding(skill: Pick<CatalogSkill, "everyone" | "holders">, agents: readonly CatalogAgent[]): Holding {
  const held = new Set(skill.holders.map((h) => h.template));
  return {
    everyone: skill.everyone,
    team: agents.filter((a) => a.kind === "standard" && held.has(a.template)),
    subagents: agents.filter((a) => a.kind === "subagent" && held.has(a.template)),
  };
}

function names(list: readonly string[]): string {
  if (list.length === 0) return "";
  if (list.length === 1) return list[0]!;
  if (list.length <= 3) return `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`;
  return `${list.slice(0, 2).join(", ")} and ${list.length - 2} more`;
}

/** "Everyone", "Everyone and Ray", "Only Julia", "Julia and Soren", "Nobody". */
export function holderWords(skill: Pick<CatalogSkill, "everyone" | "holders">, agents: readonly CatalogAgent[]): string {
  const h = holding(skill, agents);
  if (h.everyone) return h.subagents.length ? `Everyone and ${names(h.subagents.map((a) => a.name))}` : "Everyone";
  const all = [...h.team, ...h.subagents].map((a) => a.name);
  if (all.length === 0) return "Nobody";
  if (all.length === 1) return `Only ${all[0]}`;
  return names(all);
}

// ─── where it came from ────────────────────────────────────────────────

/** The drawer's line under the title: "Made by Soren, 3 Oct", "From the library", "Built in". */
export function sourceLine(
  skill: Pick<CatalogSkill, "source" | "group">,
  nameOf: (t: string) => string,
  now?: Date,
): string {
  if (skill.group === "platform") return "Built in";
  if (skill.source.kind === "clawhub") {
    const when = skill.source.at ? formatDate(skill.source.at, { weekday: false }, now) : "";
    return skill.group === "library" ? "From the library" : `From the library${when ? `, added ${when}` : ""}`;
  }
  const who = skill.source.by ? nameOf(skill.source.by) : "";
  const when = skill.source.at ? formatDate(skill.source.at, { weekday: false }, now) : "";
  const verb = skill.source.kind === "subagent_package" ? "Came with" : "Made by";
  if (!who) return when ? `Made ${when}` : "";
  return `${verb} ${who}${when ? `, ${when}` : ""}`;
}

// ─── a switch in "Who has it" ──────────────────────────────────────────

export type TeamChange =
  /** The skill's chosen agents become `agents` (null = everyone); then switch these subagents on through their own list. */
  | { kind: "assign"; agents: string[] | null; subagentsOn: string[] }
  /** Nothing to send. */
  | { kind: "none" }
  /** Refused, in words. */
  | { kind: "refuse"; why: string };

/**
 * What switching a team agent (or "everyone") on or off sends, for a
 * learned skill for the workspace. Switching one agent off on an
 * Everyone skill turns it into chosen agents: everyone else who had it,
 * the subagents that listed it included (agents added later will not get
 * it). Switching Everyone on sends null; a subagent that had it as a
 * chosen agent keeps it through its own skill list (`subagentsOn`).
 * The last holder cannot be switched off: that is Remove.
 */
export function planTeamChange(
  skill: Pick<CatalogSkill, "everyone" | "holders" | "scope">,
  agents: readonly CatalogAgent[],
  target: string | "*",
  on: boolean,
): TeamChange {
  if (skill.scope !== "customer") return { kind: "refuse", why: "Only its own agent has it" };
  const h = holding(skill, agents);
  const held = [...h.team, ...h.subagents].map((a) => a.template);
  if (target === "*") {
    if (on === h.everyone) return { kind: "none" };
    if (on) return { kind: "assign", agents: null, subagentsOn: h.subagents.map((a) => a.template) };
    if (held.length === 0) return { kind: "refuse", why: "Remove the skill instead" };
    return { kind: "assign", agents: held, subagentsOn: [] };
  }
  const has = held.includes(target);
  if (on === has) return { kind: "none" };
  const next = on ? [...held, target] : held.filter((t) => t !== target);
  if (next.length === 0) return { kind: "refuse", why: "Remove the skill instead" };
  // The last team agent switched on: everyone again (as Everyone on), so
  // switching one agent off and on again leaves the skill as it was.
  const team = agents.filter((a) => a.kind === "standard").map((a) => a.template);
  if (on && team.length > 0 && team.every((t) => next.includes(t))) {
    return { kind: "assign", agents: null, subagentsOn: h.subagents.map((a) => a.template) };
  }
  // Keep the roster's order, so the list reads the same every time.
  const order = agents.map((a) => a.template);
  next.sort((a, b) => order.indexOf(a) - order.indexOf(b));
  return { kind: "assign", agents: next, subagentsOn: [] };
}

// ─── links ─────────────────────────────────────────────────────────────

export const learnedKey = (id: number) => `learned:${id}`;

/** ?tab= links from before the page was one list: review = the waiting band, library = the library group. */
export function legacyTarget(tab: string | null): "waiting" | "library" | null {
  if (tab === "review") return "waiting";
  if (tab === "library") return "library";
  return null;
}

// ─── history ───────────────────────────────────────────────────────────

/** One history line, without who and when: "Now for Julia and Soren". */
export function eventWords(
  event: { action: string; agents?: string[] | null; detail?: Record<string, unknown> | null },
  nameOf: (t: string) => string,
): string {
  const detail = event.detail ?? {};
  const who = (list: string[] | null | undefined) =>
    list && list.length ? names(list.map(nameOf)) : "everyone";
  switch (event.action) {
    case "approve":
      return `Approved for ${who(event.agents)}`;
    case "reject":
      return "Rejected";
    case "edit":
      return typeof detail.version === "number" ? `Edited (version ${detail.version})` : "Edited";
    case "agents":
      return `Now for ${who(event.agents)}`;
    case "remove":
      return "Removed";
    case "restore":
      return "Restored";
    case "library_add":
      return detail.status === "pending_review" ? "Added from the library, waiting for review" : "Added from the library";
    case "subagent": {
      const sub = typeof detail.subagent === "string" ? nameOf(detail.subagent) : "A subagent";
      return detail.on ? `${sub} switched on` : `${sub} switched off`;
    }
    default:
      return event.action;
  }
}
